import type Phaser from "phaser";

export const TEX = {
	glow: "glow",
	spark: "spark",
	ring: "ring",
	spikes: "spikes",
	bat: "bat",
	ghost: "ghost",
	sky: "sky",
} as const;
/** The bat texture's size in pixels (wingspan × height). */
export const BAT_TEX_W = 160;
export const BAT_TEX_H = 80;
/** The ghost texture's size in pixels (square, like the emoji). */
export const GHOST_TEX_SIZE = 220;
/** Size of the glow and ring textures in pixels (scale = wanted diameter / GLOW_SIZE). */
export const GLOW_SIZE = 128;

/** Draws the generated textures (soft glow, spark, ring, night sky) once per game. */
export function createTextures(scene: Phaser.Scene): void {
	if (scene.textures.exists(TEX.glow)) return;
	add(scene, TEX.glow, GLOW_SIZE, GLOW_SIZE, (ctx, s) => {
		const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
		g.addColorStop(0, "rgba(255,255,255,1)");
		g.addColorStop(0.25, "rgba(255,255,255,0.85)");
		g.addColorStop(0.55, "rgba(255,255,255,0.3)");
		g.addColorStop(1, "rgba(255,255,255,0)");
		ctx.fillStyle = g;
		ctx.fillRect(0, 0, s, s);
	});
	add(scene, TEX.spark, 32, 32, (ctx, s) => {
		const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
		g.addColorStop(0, "rgba(255,255,255,1)");
		g.addColorStop(0.4, "rgba(255,255,255,0.7)");
		g.addColorStop(1, "rgba(255,255,255,0)");
		ctx.fillStyle = g;
		ctx.fillRect(0, 0, s, s);
	});
	add(scene, TEX.ring, GLOW_SIZE, GLOW_SIZE, (ctx, s) => {
		ctx.strokeStyle = "rgba(255,255,255,1)";
		ctx.lineWidth = s * 0.06;
		ctx.beginPath();
		ctx.arc(s / 2, s / 2, s / 2 - ctx.lineWidth, 0, Math.PI * 2);
		ctx.stroke();
	});
	// A trick light's jagged ring: an eight-pointed star outline.
	add(scene, TEX.spikes, GLOW_SIZE, GLOW_SIZE, (ctx, s) => {
		const c = s / 2;
		ctx.strokeStyle = "rgba(255,255,255,1)";
		ctx.lineWidth = s * 0.05;
		ctx.lineJoin = "miter";
		ctx.beginPath();
		for (let i = 0; i <= 16; i++) {
			const r = i % 2 === 0 ? c - ctx.lineWidth : c * 0.62;
			const a = (i / 16) * Math.PI * 2;
			ctx[i === 0 ? "moveTo" : "lineTo"](c + r * Math.cos(a), c + r * Math.sin(a));
		}
		ctx.stroke();
	});
	// A bat: black silhouette with scalloped wings and a purple rim, so it shows on the night sky.
	add(scene, TEX.bat, BAT_TEX_W, BAT_TEX_H, (ctx, w, h) => {
		const c = w / 2;
		ctx.beginPath();
		ctx.moveTo(c, h * 0.3);
		ctx.quadraticCurveTo(c - w * 0.18, h * 0.05, c - w * 0.48, h * 0.18);
		for (const [x, y] of [
			[0.4, 0.62],
			[0.3, 0.5],
			[0.2, 0.72],
			[0.1, 0.58],
			[0.06, 0.82],
		] as const) {
			ctx.quadraticCurveTo(c - w * (x + 0.04), h * (y - 0.12), c - w * x, h * y);
		}
		ctx.lineTo(c - w * 0.05, h * 0.62);
		ctx.lineTo(c - w * 0.06, h * 0.12);
		ctx.lineTo(c - w * 0.02, h * 0.3);
		ctx.lineTo(c + w * 0.02, h * 0.3);
		ctx.lineTo(c + w * 0.06, h * 0.12);
		ctx.lineTo(c + w * 0.05, h * 0.62);
		for (const [x, y] of [
			[0.06, 0.82],
			[0.1, 0.58],
			[0.2, 0.72],
			[0.3, 0.5],
			[0.4, 0.62],
		] as const) {
			ctx.quadraticCurveTo(c + w * (x - 0.04), h * (y - 0.12), c + w * x, h * y);
		}
		ctx.lineTo(c + w * 0.48, h * 0.18);
		ctx.quadraticCurveTo(c + w * 0.18, h * 0.05, c, h * 0.3);
		ctx.closePath();
		ctx.fillStyle = "#0b0612";
		ctx.fill();
		ctx.strokeStyle = "rgba(176,107,255,0.9)";
		ctx.lineWidth = 3;
		ctx.stroke();
		// Eyes.
		ctx.fillStyle = "#ffd700";
		for (const dx of [-0.025, 0.025]) {
			ctx.beginPath();
			ctx.arc(c + w * dx, h * 0.4, 2.5, 0, Math.PI * 2);
			ctx.fill();
		}
	});
	// The ghost is the same 👻 as in the menu and on the rules page, so it looks like this device's.
	add(scene, TEX.ghost, GHOST_TEX_SIZE, GHOST_TEX_SIZE, (ctx, s) => {
		ctx.font = `${s * 0.8}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
		ctx.textAlign = "center";
		ctx.textBaseline = "alphabetic";
		const m = ctx.measureText("👻");
		const y = s / 2 + (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
		ctx.fillText("👻", s / 2, y);
	});
	add(scene, TEX.sky, 4, 512, (ctx, _w, h) => {
		const g = ctx.createLinearGradient(0, 0, 0, h);
		g.addColorStop(0, "#07051a");
		g.addColorStop(0.6, "#1c0f3a");
		g.addColorStop(1, "#3a1a4a");
		ctx.fillStyle = g;
		ctx.fillRect(0, 0, 4, h);
	});
}

function add(
	scene: Phaser.Scene,
	key: string,
	w: number,
	h: number,
	draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void,
): void {
	const canvas = document.createElement("canvas");
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext("2d");
	if (!ctx) return;
	draw(ctx, w, h);
	scene.textures.addCanvas(key, canvas);
}
